import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import { Dropdown } from "react-bootstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from "../../commanComponents/Loader";
import { communication } from "../../constants/apis/communication";
import { formatDateToDDMMYYYY } from "../../constants/helpers/helperFunctions";
import { getUserDetails } from "../../constants/helpers/getUserDetails";
import "./report.css";

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ val }) => {
  const badgeColor = () => {
    if (!val) return { bg: "#718096", color: "#fff" };
    const v = val.toLowerCase();
    if (v === "good" || v === "ok") return { bg: "#ffc107", color: "#000" };
    if (v === "notok" || v === "not ok") return { bg: "#dc3545", color: "#fff" };
    if (v === "new") return { bg: "#e3f2fd", color: "#1976d2" };
    if (v === "approved") return { bg: "#e8f5e9", color: "#2e7d32" };
    if (v === "rejected" || v === "reassigned") return { bg: "#fff3e0", color: "#ef6c00" };
    if (v === "pending_supervisor") return { bg: "#cfd8dc", color: "#263238" };
    if (v === "pending_hod") return { bg: "#ede7f6", color: "#5e35b1" };
    return { bg: "#718096", color: "#fff" };
  };

  const { bg, color } = badgeColor();
  return (
    <span
      style={{
        backgroundColor: bg,
        color: color,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        textTransform: "capitalize",
        display: "inline-block",
      }}
    >
      {val?.split("_").join(" ")}
    </span>
  );
};

export default function ReportComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mineId = searchParams.get("mineId");

  let reportId = searchParams.get("reportId");
  let allReportIdsStr = reportId;

  if (!reportId) {
    try {
      const sessionIds = JSON.parse(sessionStorage.getItem("selectedReportIds") || "[]");
      if (sessionIds && sessionIds.length > 0) {
        reportId = sessionIds[0];
        allReportIdsStr = sessionIds.join(",");
      }
    } catch (e) {
      console.error("Error parsing session report ids", e);
    }
  }

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  const userDetails = getUserDetails();
  const designationArr = Array.isArray(userDetails?.designation) ? userDetails.designation : [userDetails?.designation || userDetails?.userType || ""];

  const isHodOrAbove = designationArr.some((role) => {
    const roleStr = String(role || "").toLowerCase();
    return roleStr === "hod" || roleStr === "hod-mining" || roleStr === "hod-mechanical";
  });

  const { data: logDetails, isFetching, refetch } = useQuery(["getLogById", allReportIdsStr, mineId], () => communication?.getLogById({ id: allReportIdsStr, mineId }), {
    enabled: !!allReportIdsStr,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data?.status === "JWT_INVALID" || data?.data?.status === "FAILED") {
        toast.error(`Failed: ${data?.data?.message}`);
      } else if (data?.data?.log) {
        setFormData(Array.isArray(data.data.log) ? data.data.log : data.data.log ? [data.data.log] : []);
      }
    },
    onError: (error) => {
      toast.error(`Failed: ${error?.message}`);
    },
  });

  const logData = Array.isArray(logDetails?.data?.log) ? logDetails.data.log : logDetails?.data?.log ? [logDetails.data.log] : [];

  // Define columns mapped exactly to the required response fields
  const allAvailableColumns = [
    { header: "Sr. No", key: "srNo", editable: false, alwaysShow: true },
    { header: "Machine Name", key: "machineName", editable: false },
    { header: "Equipment Number", key: "equipmentNumber", editable: false },
    { header: "Equipment Category", key: "equipmentCategory", editable: false },
    { header: "Mine Name", key: "mineName", editable: false },
    { header: "Activity Date", key: "activityDate", editable: false },
    { header: "Shift", key: "shift", editable: false },
    { header: "Time From", key: "timeFrom", editable: true },
    { header: "Time To", key: "timeTo", editable: true },
    { header: "Load Time", key: "loadTime", editable: true },
    { header: "Total Run", key: "totalRun", editable: true },
    { header: "BD Hrs", key: "bdHrs", editable: true },
    { header: "Breakdown Hrs", key: "breakdownHrs", editable: true },
    { header: "Maintenance Work", key: "maintenanceWork", editable: true },
    { header: "Work Done", key: "workDone", editable: true },
    { header: "Maintenance Hrs", key: "maintenanceHrs", editable: true },
    { header: "Re-Handling", key: "reHandling", editable: true },
    { header: "Ideal Hrs", key: "idealHrs", editable: true },
    { header: "Operator Name", key: "operatorName", editable: false },
    { header: "Machine Status", key: "machineStatus", editable: false },
    { header: "Mechanic Name", key: "mechanicName", editable: true },
    // { header: "Mechanical Section Incharge", key: "mechanicalSectionIncharge", editable: true },
    { header: "Remark", key: "remark", editable: true },
    { header: "Description", key: "description", editable: true },
    { header: "Hydraulic Oil Level", key: "hydraulicOilLevel", editable: true },
    { header: "Line Voltage", key: "lineVoltage", editable: true },
    { header: "Hydraulic Hoses", key: "hydraulicHoses", editable: true },
    { header: "Head And Tail Light", key: "headAndTailLight", editable: true },
    { header: "Audible Warning Device", key: "audibleWarningDevice", editable: true },
    { header: "Tyre And Disc", key: "tyreAndDisc", editable: true },
    { header: "Locking Plates", key: "lockingPlates", editable: true },
    { header: "Effectiveness Of Brake", key: "effectivenessOfBrake", editable: true },
    { header: "Abnormal Sound And Vibration", key: "abnormalSoundAndVibration", editable: true },
    { header: "Machine Cleanliness", key: "machineCleanliness", editable: true },
    { header: "Gear Oil Level", key: "gearOilLevel", editable: true },
    { header: "Battery Situation", key: "batterySituation", editable: true },
    { header: "Horn Situation", key: "hornSituation", editable: true },
    { header: "Electrician Name", key: "electricianName", editable: true },
    { header: "Break Situation", key: "breakSituation", editable: true },
    { header: "Light Situation", key: "lightSituation", editable: true },
    { header: "Canopy Situation", key: "canopySituation", editable: true },
    { header: "Popular Shaft Situation", key: "popularShaftSituation", editable: true },
    { header: "Axle Situation", key: "axleSituation", editable: true },
    { header: "Wheels Situation", key: "wheelsSituation", editable: true },
    { header: "Driver Name", key: "driverName", editable: true },
    { header: "Details Of Work", key: "detailsOfWork", editable: true },
    { header: "Initial Reading", key: "initialReading", editable: true },
    { header: "Final Reading", key: "finalReading", editable: true },
    { header: "Total Km Run", key: "totalKmRun", editable: true },
    { header: "Diesel Oil Issue", key: "dieselOilIssue", editable: true },
    { header: "Employee Name", key: "employeeName", editable: true },
    { header: "Water Pump Pressure", key: "waterPumpPressure", editable: true },
    { header: "Air And Discharge Pressure", key: "airAndDischargePressure", editable: true },
    { header: "Schedule Hrs", key: "scheduleHrs", editable: true },
    { header: "Pump Pressure", key: "pumpPressure", editable: true },
    { header: "Separator Pressure", key: "separatorPressure", editable: true },
    { header: "Oil Consumed", key: "oilConsumed", editable: true },
    { header: "Breakdown Reason", key: "breakdownReason", editable: true },
    { header: "Track Tension Check", key: "trackTensionCheck", editable: true },
    { header: "Hauge Leakage Checking", key: "haugeLeakageChecking", editable: true },
    { header: "Hydraulic Cable", key: "hydraulicCable", editable: true },
    { header: "Cleaning Machine", key: "cleaningMachine", editable: true },
    { header: "Head Lift Situation", key: "headLiftSituation", editable: true },
    { header: "Sprocket Situation", key: "sprocketSituation", editable: true },
    { header: "Idealer Situation", key: "idealerSituation", editable: true },
    { header: "Roller Situation", key: "rollerSituation", editable: true },
    { header: "Oil Temp", key: "oilTemp", editable: true },
    { header: "Main Brake", key: "mainBrake", editable: true },
    { header: "Secondary Brake", key: "secondaryBrake", editable: true },
    { header: "Over Hoist", key: "overHoist", editable: true },
    { header: "Over Wind", key: "overWind", editable: true },
    { header: "Depth Indicator", key: "depthIndicator", editable: true },
    { header: "Signal Ball", key: "signalBall", editable: true },
    { header: "Camera", key: "camera", editable: true },
    { header: "Gate Keps Indication", key: "gateKepsIndication", editable: true },
    { header: "Hydraulic Hoses Condition", key: "hydraulicHosesCondition", editable: true },
    { header: "Engine Oil Level", key: "engineOilLevel", editable: true },
    { header: "Coolent Level", key: "coolentLevel", editable: true },
    { header: "Rear View Mirror Camera", key: "rearViewMirrorCamera", editable: true },
    { header: "Horn", key: "horn", editable: true },
    { header: "Fire Extinguisher", key: "fireExtinguisher", editable: true },
    { header: "Afdss", key: "afdss", editable: true },
    { header: "Operator Seat Belt", key: "operatorSeatBelt", editable: true },
    { header: "Air Conditioner", key: "airConditioner", editable: true },
    { header: "Side Indicator", key: "sideIndicator", editable: true },
    { header: "Ava", key: "ava", editable: true },
    { header: "Emergency Steering System", key: "emergencySteeringSystem", editable: true },
    { header: "Steering System", key: "steeringSystem", editable: true },
    { header: "Brake System", key: "brakeSystem", editable: true },
    { header: "Tyre Condition Pressure", key: "tyreConditionPressure", editable: true },
    { header: "Trips", key: "trips", editable: true },
    { header: "Drilling Meters", key: "drillingMeters", editable: true },
    { header: "Pto Swing Gb Oil Level", key: "ptoSwingGbOilLevel", editable: true },
    { header: "Track Condition", key: "trackCondition", editable: true },
    { header: "Bucket Condition", key: "bucketCondition", editable: true },
    { header: "Hydraulic Function", key: "hydraulicFunction", editable: true },
    { header: "Transmission Oil Level", key: "transmissionOilLevel", editable: true },
    { header: "Compressor Oil Level", key: "compressorOilLevel", editable: true },
    { header: "Hammer Button Bit Condition", key: "hammerButtonBitCondition", editable: true },
    { header: "Hydaulic Functions", key: "hydaulicFunctions", editable: true },
    { header: "Transmission Gear Box Condition", key: "transmissionGearBoxCondition", editable: true },
    { header: "Break Down Hrs", key: "breakDownHrs", editable: true },
    { header: "Output Plus 25mm", key: "outputPlus25mm", editable: true },
    { header: "Output Plus 10mm", key: "outputPlus10mm", editable: true },
    { header: "Output Minus 10mm", key: "outputMinus10mm", editable: true },
    { header: "Output Fines", key: "outputFines", editable: true },
    { header: "Total Output", key: "totalOutput", editable: true },
    { header: "Emergancy Stop Switch", key: "emergancyStopSwitch", editable: true },
    { header: "Gear Boxes Oil Level", key: "gearBoxesOilLevel", editable: true },
    { header: "Jaw Crusher Oil Level", key: "jawCrusherOilLevel", editable: true },
    { header: "Conveyor Belt Condition", key: "conveyorBeltCondition", editable: true },
    { header: "Pull Cord", key: "pullCord", editable: true },
    { header: "V Belts Condition", key: "vBeltsCondition", editable: true },
    { header: "Safety Hooter", key: "safetyHooter", editable: true },
    { header: "Rollers Condition", key: "rollersCondition", editable: true },
    { header: "Pull Cord Switch", key: "pullCordSwitch", editable: true },
    { header: "Zero Stop Switch", key: "zeroStopSwitch", editable: true },
    { header: "Safety System", key: "safetySystem", editable: true },
    { header: "Bunker Condition", key: "bunkerCondition", editable: true },
    { header: "Feeder Condition", key: "feederCondition", editable: true },
    { header: "Crusher Condition", key: "crusherCondition", editable: true },
    { header: "Converyor Belt Condition", key: "converyorBeltCondition", editable: true },
    { header: "Exciter Condition", key: "exciterCondition", editable: true },
    { header: "Coupling Condition", key: "couplingCondition", editable: true },
    { header: "Sump", key: "sump", editable: true },
    { header: "Water Supply Pumps", key: "waterSupplyPumps", editable: true },
    { header: "Chute Hoppers", key: "chuteHoppers", editable: true },
    { header: "Motors Conditions", key: "motorsConditions", editable: true },
    { header: "Condition Of Battery", key: "conditionofBattery", editable: true },
    { header: "Battery Level", key: "batteryLevel", editable: true },
    { header: "Weather Protection", key: "weatherProtection", editable: true },
    { header: "Emergency Stop", key: "emergencyStop", editable: true },
    { header: "Fuel Safety", key: "fuelSafety", editable: true },
    { header: "Sound", key: "sound", editable: true },
    { header: "Auto Start Stop System", key: "autoStartStopSystem", editable: true },
    { header: "Created By", key: "createdBy", editable: false, alwaysShow: true },
    { header: "Supervisor Name", key: "supervisorId", editable: false, alwaysShow: true },
    { header: "Approval Status", key: "approvalStatus", editable: false, alwaysShow: true },
  ];

  const getNestedValue = (row, key) => {
    if (!row) return "";
    switch (key) {
      case "equipmentNumber":
        return row.equipmentNumber || row.equipmentId?.equipmentId || "";
      case "equipmentCategory":
        return row.equipmentCategory || row.equipmentId?.equipmentCategory || "";
      case "machineName":
        return row.machineName || row.equipmentId?.equipmentDescription || "";
      case "shift":
        return typeof row.shift === "object" ? row.shift?.shift : row.shift || "";
      case "miningShiftIncharge":
        return typeof row.miningShiftIncharge === "object" ? row.miningShiftIncharge?.name : row.miningShiftIncharge || "";
      case "supervisorId":
        return typeof row.supervisorId === "object" ? row.supervisorId?.name : row.supervisorId || "";
      case "createdBy":
        return typeof row.createdBy === "object" ? row.createdBy?.name : row.createdBy || "";
      case "bdHrs":
        return row.bdHrs || row.BDHrs || "";
      case "breakdownHrs":
        return row.breakdownHrs || row.breakDownHours || "";
      case "reHandling":
        return row.reHandling || row.rehandling || "";
      default:
        return row[key] !== undefined && row[key] !== null ? row[key] : "";
    }
  };

  const columns = allAvailableColumns.filter((col) => {
    if (col.alwaysShow) return true;
    return logData.some((row) => {
      const val = getNestedValue(row, col.key);
      return val !== "" && val !== "--";
    });
  });

  const handleInputChange = (e, key, rowIndex) => {
    setFormData((prev) => {
      const newData = [...prev];
      newData[rowIndex] = {
        ...newData[rowIndex],
        [key]: e.target.value,
      };
      return newData;
    });
  };

  const handleSave = async () => {
    if (!formData || formData.length === 0) {
      toast.error("No data to save");
      return;
    }

    try {
      const loadingToastId = toast.loading("Saving logs...");
      const response = await communication.bulkUpdateLog({ logs: formData, mineId });
      toast.dismiss(loadingToastId);
      
      if (response?.data?.status === "SUCCESS") {
        toast.success(response.data.message || "Logs updated successfully");
        setEditMode(false);
        if (refetch) refetch();
      } else {
        toast.error(response?.data?.message || "Failed to update logs");
      }
    } catch (error) {
      toast.dismiss();
      toast.error("An error occurred while saving");
    }
  };

  const handleApprove = async () => {
    if (!allReportIdsStr) {
      toast.error("No report selected to approve");
      return;
    }
    
    try {
      const loadingToastId = toast.loading("Approving logs...");
      const payload = {
        logIds: allReportIdsStr.split(","),
        updateFields: { approvalStatus: "approved" },
        mineId
      };
      
      const response = await communication.bulkUpdateLog(payload);
      toast.dismiss(loadingToastId);
      
      if (response?.data?.status === "SUCCESS") {
        toast.success(response.data.message || "Logs approved successfully");
        if (refetch) refetch();
      } else {
        toast.error(response?.data?.message || "Failed to approve logs");
      }
    } catch (error) {
      toast.dismiss();
      toast.error("An error occurred while approving");
    }
  };


  const getCellValue = (col, rowIndex) => {
    const key = col.key;
    const dataSource = editMode ? formData[rowIndex] || {} : logData[rowIndex] || {};

    if (editMode && col.editable) {
      const isOkNotOkColumn = logData.some((row) => {
        const val = String(row[key] || "").toLowerCase().replace(/\s/g, "");
        return ["ok", "notok", "good"].includes(val);
      });

      if (isOkNotOkColumn) {
        return (
          <select
            value={dataSource[key] || ""}
            onChange={(e) => handleInputChange(e, key, rowIndex)}
            style={{
              padding: "4px 8px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "12px",
              width: "120px",
              outline: "none",
            }}
          >
            <option value="">Select</option>
            <option value="ok">Ok</option>
            <option value="not ok">Not Ok</option>
          </select>
        );
      }

      return (
        <input
          type="text"
          value={dataSource[key] || ""}
          onChange={(e) => handleInputChange(e, key, rowIndex)}
          style={{
            padding: "4px 8px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "12px",
            width: "120px",
            outline: "none",
          }}
        />
      );
    }

    switch (key) {
      case "srNo":
        return String(rowIndex + 1);
      case "activityDate":
        return formatDateToDDMMYYYY(dataSource.activityDate || dataSource.logbookDate || dataSource.createdAt) || "--";
      case "approvalStatus":
        return dataSource.approvalStatus || dataSource.status ? <StatusBadge val={dataSource.approvalStatus || dataSource.status} /> : "--";
      case "machineStatus":
        return dataSource.machineStatus ? <StatusBadge val={dataSource.machineStatus} /> : "--";
      case "equipmentNumber":
      case "equipmentCategory":
      case "machineName":
      case "bdHrs":
      case "breakdownHrs":
      case "reHandling":
      case "shift":
      case "miningShiftIncharge":
      case "supervisorId":
      case "createdBy":
        return getNestedValue(dataSource, key) || "--";
      default:
        if (dataSource[key] && typeof dataSource[key] === "object") return "--";
        return dataSource[key] || "--";
    }
  };

  // ─── Export Logic ─────────────────────────────────────────────────────────
  const getDateTimeString = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  };

  const getExportData = () => {
    return logData.map((rowLog, rowIndex) => {
      const rowDataOut = {};
      const currentLog = editMode ? formData[rowIndex] || {} : rowLog;
      columns.forEach((col) => {
        let val = getNestedValue(currentLog, col.key) || "--";
        if (col.key === "srNo") val = String(rowIndex + 1);
        else if (col.key === "activityDate") val = formatDateToDDMMYYYY(currentLog.activityDate || currentLog.createdAt) || "--";
        else if (col.key === "approvalStatus") val = currentLog.approvalStatus || currentLog.status || "--";

        // Remove React nodes from export text
        if (typeof val === "object" && val !== null) {
          if (val.props && val.props.val) {
            val = val.props.val;
          } else {
            val = "--";
          }
        }

        rowDataOut[col.header] = val;
      });
      return rowDataOut;
    });
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(getExportData());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daily Activity Report");
    XLSX.writeFile(wb, `Daily_Activity_Report_${getDateTimeString()}.xlsx`);
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(getExportData());
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Daily_Activity_Report_${getDateTimeString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    try {
      const logsArray = editMode ? formData : logData;
      const firstLog = logsArray[0] || {};

      // 1. Initialize Landscape A4 Document
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });

      const pageWidth = pdf.internal.pageSize.getWidth(); // 297mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm
      const margin = 10;
      const usableWidth = pageWidth - margin * 2; // 277mm

      // ─── 2. HEADER SECTION ────────────────────────────────────────────────
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      const mineTitle = `MOIL LIMITED - ${(firstLog.mineName || "DONGRI BUZURG MINE").toUpperCase()}`;
      pdf.text(mineTitle, pageWidth / 2, 14, { align: "center" });

      pdf.setFontSize(11);
      pdf.text("DAILY ACTIVITY REPORT", pageWidth / 2, 20, { align: "center" });

      // ─── 3. SUB-HEADER METADATA BLOCK ────────────────────────────────────
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");

      // Left side metadata
      pdf.text(`Equipment No: ${firstLog.equipmentNumber || "--"}`, margin, 28);
      pdf.text(`Machine Name: ${firstLog.machineName || "--"}`, margin, 33);
      pdf.text(`Machine Status: ${(firstLog.machineStatus || "--").toUpperCase()}`, margin, 38);

      // Right side metadata
      const dateStr = formatDateToDDMMYYYY(firstLog.activityDate || firstLog.createdAt || new Date());
      pdf.text(`DATE: ${dateStr}`, pageWidth - margin, 28, { align: "right" });
      pdf.text(`SHIFT: ${firstLog.shift?.shift || "--"}`, pageWidth - margin, 33, { align: "right" });
      pdf.text(`Category: ${firstLog.equipmentCategory || "--"}`, pageWidth - margin, 38, { align: "right" });

      // ─── 4. DATA COMPILATION ─────────────────────────────────────────────
      const tableHeaders = columns.map((col) => col.header);
      const secondaryHeaders = columns.map((_, idx) => String(idx + 1));

      const dataRows = logsArray.map((currentLog, rowIndex) => {
        return columns.map((col) => {
          const key = col.key;
          if (key === "srNo") return String(rowIndex + 1);
          if (key === "activityDate") return dateStr;
          if (key === "createdBy") return currentLog.createdBy?.name || currentLog.createdBy || "--";
          if (key === "supervisorId") return currentLog.supervisorId?.name || currentLog.supervisorId || "--";
          if (key === "bdHrs") return currentLog.bdHrs || currentLog.BDHrs || "";
          if (key === "breakdownHrs") return currentLog.breakdownHrs || currentLog.breakDownHours || "";
          if (key === "reHandling") return currentLog.reHandling || currentLog.rehandling || "";
          if (key === "workDone") return currentLog.workDone || "";
          if (key === "miningShiftIncharge") return currentLog.miningShiftIncharge?.name || currentLog.miningShiftIncharge || "";
          if (key === "shift") return currentLog.shift?.shift || currentLog.shift || "--";
          if (currentLog[key] && typeof currentLog[key] === "object") return "--";
          return currentLog[key] !== undefined && currentLog[key] !== null ? String(currentLog[key]) : "";
        });
      });

      // ─── 5. AUTOTABLE MATRIX GENERATION ──────────────────────────────────
      const totalCols = columns.length;
      const srNoWidth = 8;
      const remainingWidth = usableWidth - srNoWidth;
      const defaultColWidth = remainingWidth / (totalCols - 1);

      const columnStyles = {};
      columns.forEach((col, idx) => {
        if (col.key === "srNo") {
          columnStyles[idx] = { cellWidth: srNoWidth };
        } else {
          columnStyles[idx] = { cellWidth: defaultColWidth };
        }
      });

      autoTable(pdf, {
        startY: 44,
        margin: { left: margin, right: margin },
        head: [
          [
            {
              content: "Daily Operations Allocation",
              colSpan: columns.length,
              styles: { halign: "center", fontStyle: "bold", fillColor: [243, 244, 246], textColor: [17, 24, 39], fontSize: 9, cellPadding: 2.5 },
            },
          ],
          tableHeaders,
          secondaryHeaders,
        ],
        body: dataRows,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: "hidden",
          halign: "center",
          valign: "middle",
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.15,
        },
        columnStyles: columnStyles,
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
        didParseCell: function (data) {
          if (data.row.index === 1 && data.section === "head") {
            // Give enough room for vertical rotated text titles
            data.cell.styles.minCellHeight = 40;
          }
          if (data.row.index === 2 && data.section === "head") {
            data.cell.styles.fillColor = [243, 244, 246];
            data.cell.styles.fontSize = 6.5;
          }
        },
        willDrawCell: function (data) {
          if (data.row.index === 1 && data.section === "head") {
            pdf.saveGraphicsState();

            // Calculate exact visual center of the cell box area
            const cx = data.cell.x + data.cell.width / 2;
            const cy = data.cell.y + data.cell.height - 2;

            pdf.setFontSize(6.2);
            pdf.setFont("helvetica", "bold");

            const headerText = data.cell.raw ? String(data.cell.raw) : data.cell.text.join(" ") || "";

            // Safe vertical string generation strategy without overriding layout alignment keys
            pdf.text(headerText, cx, cy, {
              angle: 45,
            });

            pdf.restoreGraphicsState();
            data.cell.text = [];
            return false;
          }
        },
      });

      // ─── 6. SIGNATURE FOOTER TRACK ───────────────────────────────────────
      const finalTableY = pdf.lastAutoTable.finalY || 100;
      const signatureY = Math.min(finalTableY + 22, pageHeight - 20);

      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");

      // Left Section: Verified Status Badge
      if (firstLog.approvalStatus || firstLog.status) {
        const rawStatus = firstLog.approvalStatus || firstLog.status;
        const statusLower = rawStatus.toLowerCase();

        let textColor = [22, 101, 52]; // default green (approved)
        if (statusLower.includes("pending")) textColor = [239, 108, 0]; // orange
        if (statusLower.includes("reject")) textColor = [220, 53, 69]; // red

        pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
        const statusText = `[ ${rawStatus.split("_").join(" ").toUpperCase()} ]`;
        pdf.text(statusText, margin + 25, signatureY - 6, { align: "center" });
      }

      pdf.setTextColor(0, 0, 0);
      pdf.text(firstLog.operatorName || "--", margin + 25, signatureY, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      pdf.text("Operator / Mine Mate", margin + 25, signatureY + 4, { align: "center" });

      // Middle Section: Shift Incharge
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(0, 0, 0);
      pdf.text(firstLog.miningShiftIncharge?.name || "-", pageWidth / 2, signatureY, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      pdf.text("Shift Incharge", pageWidth / 2, signatureY + 4, { align: "center" });

      // Right Section: Supervisor / Manager
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(0, 0, 0);
      const supervisorName = firstLog.supervisorId?.name || firstLog.supervisorId || "--";
      pdf.text(supervisorName, pageWidth - margin - 25, signatureY, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      pdf.text("Supervisor", pageWidth - margin - 25, signatureY + 4, { align: "center" });

      // Save PDF output
      pdf.save(`Daily_Activity_Report_${getDateTimeString()}.pdf`);
      toast.success("PDF generated successfully");
    } catch (err) {
      console.error("PDF Export error details: ", err);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div style={{ backgroundColor: "#f3f4f6", minHeight: "100vh", padding: "32px" }}>
      {isFetching && <Loader />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#111827", margin: 0 }}>Daily Activity Report</h2>

        <div style={{ display: "flex", gap: "12px" }}>
          {editMode ? (
            <>
              <button
                onClick={handleSave}
                style={{
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s",
                }}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setFormData(logData);
                  setEditMode(false);
                }}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s",
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {isHodOrAbove && logData?.some(log => log.approvalStatus === "pending_hod") && (
                <>
                  <button
                    onClick={handleApprove}
                    style={{
                      backgroundColor: "#3b82f6",
                      color: "#fff",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: "pointer",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s",
                    }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setEditMode(true)}
                    style={{
                      backgroundColor: "#2f3299",
                      color: "#fff",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: "pointer",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s",
                    }}
                  >
                    Edit
                  </button>
                </>
              )}

              <Dropdown>
                <Dropdown.Toggle
                  style={{
                    backgroundColor: "#10b981",
                    color: "#fff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontWeight: "600",
                    fontSize: "14px",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                  id="dropdown-export"
                >
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ borderRadius: "8px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e5e7eb" }}>
                  <Dropdown.Item onClick={exportToExcel} style={{ fontSize: "14px", fontWeight: "500", color: "#374151", padding: "8px 16px" }}>
                    Export to Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportToCSV} style={{ fontSize: "14px", fontWeight: "500", color: "#374151", padding: "8px 16px" }}>
                    Export to CSV
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportToPDF} style={{ fontSize: "14px", fontWeight: "500", color: "#374151", padding: "8px 16px" }}>
                    Export to PDF
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}

          <button
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: "#ffffff",
              color: "#374151",
              border: "1px solid #d1d5db",
              padding: "8px 16px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              transition: "all 0.2s",
            }}
          >
            Go Back
          </button>
        </div>
      </div>

      <div className="spreadsheet-container" style={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)" }}>
        {logData && Object.keys(logData).length > 0 ? (
          <table className="spreadsheet-table">
            <thead>
              {/* Main Headers */}
              <tr>
                {columns.map((col, index) => (
                  <th key={`header-${index}`}>{col.header}</th>
                ))}
              </tr>
              {/* Secondary Header (Numbers) matching the screenshot */}
              <tr>
                {columns.map((_, index) => (
                  <th key={`num-${index}`}>{index + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logData.map((rowLog, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {columns.map((col, colIndex) => (
                    <td key={`cell-${rowIndex}-${colIndex}`} className="spreadsheet-td-val">
                      {getCellValue(col, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          !isFetching && <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>No log details found.</div>
        )}
      </div>

      {logData?.logbook?.length > 0 && (
        <div style={{ marginTop: "32px", backgroundColor: "#fff", padding: "24px", borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginBottom: "16px", borderBottom: "1px solid #e5e7eb", paddingBottom: "8px" }}>Gang Activities (Logbook)</h3>
          {logData.logbook.map((gang, idx) => (
            <div key={idx} style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  <strong style={{ color: "#111827" }}>Gang No:</strong> {gang.gangNo || "N/A"}
                </div>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  <strong style={{ color: "#111827" }}>Gang Man:</strong> {gang.gangMan || "N/A"}
                </div>
              </div>
              {gang.logbookArray && gang.logbookArray.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table className="spreadsheet-table" style={{ width: "100%", minWidth: "auto" }}>
                    <thead>
                      <tr>
                        <th>Job Code</th>
                        <th>Job Description</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gang.logbookArray.map((job, jIdx) => (
                        <tr key={jIdx}>
                          <td>{job.jobCode || "-"}</td>
                          <td>{job.jobDescription || "-"}</td>
                          <td>{job.quantity || "-"}</td>
                          <td>{job.unit || "-"}</td>
                          <td>{job.rate || "-"}</td>
                          <td>{job.amount || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ fontSize: "13px", color: "#6b7280" }}>No activities found for this gang.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
